import { createAccess } from '@refinio/one.core/lib/access.js';
import { getInstanceIdHash, getInstanceOwnerIdHash } from '@refinio/one.core/lib/instance.js';
import { createCryptoApiFromDefaultKeys, getDefaultKeys } from '@refinio/one.core/lib/keychain/keychain.js';
import { getOnlyLatestReferencingObjsHashAndId } from '@refinio/one.core/lib/reverse-map-query.js';
import { SET_ACCESS_MODE } from '@refinio/one.core/lib/storage-base-common.js';
import { getObject } from '@refinio/one.core/lib/storage-unversioned-objects.js';
import { getIdObject, getObjectByIdHash, storeVersionedObject } from '@refinio/one.core/lib/storage-versioned-objects.js';
import { createRandomString } from '@refinio/one.core/lib/system/crypto-helpers.js';
import { calculateIdHashOfObj } from '@refinio/one.core/lib/util/object.js';
import { serializeWithType } from '@refinio/one.core/lib/util/promise.js';
import { createInstanceWithDefaultKeys, getInstancesOfPerson, getLocalInstanceOfPerson, hasPersonLocalInstance } from '../../misc/instance.js';
import { objectEvents } from '../../misc/ObjectEventDispatcher.js';
import { OEvent } from '../../misc/OEvent.js';
import { createPerson, createPersonWithDefaultKeys, isPersonComplete } from '../../misc/person.js';
import Watchdog from '../../misc/Watchdog.js';
import IoMManager from '../IoM/IoMManager.js';
import TrustedKeysManager from './TrustedKeysManager.js';
import { Model } from '../Model.js';
import GroupModel from './GroupModel.js';
import ProfileModel from './ProfileModel.js';
import SomeoneModel from './SomeoneModel.js';
const ZERO_HASH = '0'.repeat(64);
/**
 * This class manages people - to be precise: their identities including your own.
 *
 * Identity is important for defining which data belongs to whom, with whom to share data ...
 * This class is one of the few central building blocks that makes the one ecosystem tick.
 *
 * It uses three concepts to manage identities:
 * - Person:  A person is the identity used throughout the application. Connections, messages
 *            access rights are all tied to a person. The SHA256Hash of the person object is
 *            what is usually used to refer to a person, so if we speak of person-id we usually mean
 *            the SHA256Hash<Person>. Another alias for a person / person-id is 'Identity'.
 * - Profile: A profile describes a person and ways how to contact that person.
 *            Multiple profiles for the same person are supported, because we think that you don't
 *            want to share the same profile about yourself with all persons you know. Perhaps you
 *            want to share a 'good boy' profile (nice profile image) with your family, but a
 *            bad-ass profile with your friends.
 * - Someone: A real life persons might want to create multiple identities. Use cases are:
 *            - Anonymous identities (throw away identities or for dating ...)
 *            - Work Identity / Private Identity to be able to separate work from private life
 *              better compared having one identity but a work and private profile.
 *            'Someone' is a collection of Identities that belongs to a single person. For other
 *            persons you usually only know a single identity, so the someone object of this person
 *            just refers to profiles of a single identity. But for your own you will have lots of
 *            Identities. Someone is only a local mechanism to group multiple identities of the
 *            same person. It has no meaning beyond the own ONE ecosystem.
 *
 * Q: How are Person / Profile and Someone related?
 * A: Someone refers to multiple profiles, a profile refers to an identity.
 *
 * Q: What are the responsibilities of this model?
 * A:
 * 1) Manage all those identities
 *    - Create new identities
 *    - Get a list of identities / own identities
 * 2) Manage the profiles describe those identities.
 *    - create / update / delete profiles
 *    - share profiles with others / get sharing state
 *    - obtain profiles
 *
 * Other important information:
 *    - Each profile has an owner. It is a namespacing mechanism. Only the owner should write
 *    the profile. This should be enforces in the future.
 *    - The 'default' profile where personId and owner are the same has a special meaning: It is
 *    the profile that is automatically updated with ne sign keys etc, and this is also the
 *    profile that is shared by default with other people.
 */
export default class LeuteModel extends Model {
    // #### Events ####
    onProfileUpdate = new OEvent();
    onMeIdentitiesChange = new OEvent();
    // Emitted when a new instance endpoint was added to leute
    // Note: It might be emitted also for already known endpoints at the moment.
    onNewOneInstanceEndpoint = new OEvent();
    beforeMainIdSwitch = new OEvent();
    afterMainIdSwitch = new OEvent();
    // #### Events - END ####
    static EVERYONE_GROUP_NAME = 'everyone';
    commserverUrl;
    pLoadedVersion;
    leute;
    createEveryoneGroup;
    shutdownInternal = async () => {
        /*...*/
    };
    // Map that stores display names
    personNameCache = new Map();
    everyoneGroupNewPeopleCache = [];
    everyoneGroupWatchdog = new Watchdog(10000);
    trustedKeysManager = new TrustedKeysManager(this);
    get trust() {
        return this.trustedKeysManager;
    }
    /**
     * Constructor
     *
     * @param commserverUrl - when creating the default oneInstanceEndpoint this url is used
     * @param createEveryoneGroup -  If true then init() should create an everyone group and add
     * listeners for new 'Person' objects and add them if they are not in the everyone group.
     * (default: false)
     */
    constructor(commserverUrl, createEveryoneGroup = false) {
        super();
        this.commserverUrl = commserverUrl;
        this.createEveryoneGroup = createEveryoneGroup;
    }
    /**
     * Init the module.
     *
     * This will initialize the data structures for 'me': someone, profile and a
     * OneInstanceEndpoint for the current instance.
     * As main identity the owner of the main one instance is used. This might change in the future!
     */
    async init() {
        this.state.assertCurrentState('Uninitialised');
        // Reuse the instance and person from one.core
        const personId = getInstanceOwnerIdHash();
        if (personId === undefined) {
            throw new Error('The instance has no owner.');
        }
        try {
            await this.loadLatestVersion();
        }
        catch (e) {
            const instanceId = getInstanceIdHash();
            if (instanceId === undefined) {
                throw new Error('The instance is not initialized.');
            }
            const profile = await this.createInitialDefaultProfile({
                personId,
                personKeys: await getDefaultKeys(personId),
                instanceId,
                instanceKeys: await getDefaultKeys(instanceId)
            });
            const someone = await SomeoneModel.constructWithNewSomeone(this, 'me', profile);
            // Assign the leute object to the member for the saveAndLoad function
            // I know this member passing around isn't ideal. We should fix this later, to make it more
            // explicit what happens here.
            this.leute = {
                $type$: 'Leute',
                appId: 'one.leute',
                me: someone.idHash,
                other: [],
                group: []
            };
            await this.saveAndLoad();
            // Give the new main identity all rights, so that he can declare trust for other keys
            await this.givePersonAllRights(personId, personId);
        }
        const disconnectFns = [];
        disconnectFns.push(objectEvents.onNewVersion(async (result) => {
            await this.addProfileFromResult(result);
            await this.updatePersonNameCacheForPerson(result.obj.personId);
        }, 'LeuteModel: New profile version - Add profile and update person name cache', 'Profile'), objectEvents.onNewVersion(async (result) => {
            for (const [id] of result.obj.identities.entries()) {
                await this.updatePersonNameCacheForPerson(id).catch(console.error);
            }
        }, 'LeuteModel: New someone version - Update person name cache for all identities', 'Someone'), this.everyoneGroupWatchdog.onTimeout(async () => {
            await this.syncEveryoneGroup();
        }));
        if (this.createEveryoneGroup) {
            const group = await this.createGroupInternal(LeuteModel.EVERYONE_GROUP_NAME);
            if (group.persons.find(person => person === personId) === undefined) {
                group.persons.push(personId);
                await group.saveAndLoad();
            }
            disconnectFns.push(objectEvents.onNewIdObject(this.addPersonToEveryoneGroup.bind(this), 'LeuteModel: addPersonToEveryoneGroup', 'Person'));
        }
        this.shutdownInternal = async () => {
            if (this.everyoneGroupWatchdog.enabled()) {
                this.everyoneGroupWatchdog.disable();
            }
            if (this.everyoneGroupNewPeopleCache.length > 0) {
                await this.syncEveryoneGroup();
            }
            await this.trust.shutdown();
            for (const disconnectFn of disconnectFns) {
                disconnectFn();
            }
            this.leute = undefined;
            this.pLoadedVersion = undefined;
            this.personNameCache.clear();
            this.shutdownInternal = async () => {
                /*...*/
            };
        };
        this.state.triggerEvent('init');
        await this.trust.init();
        await this.updatePersonNameCache();
    }
    /**
     * Shutdown the leute model
     */
    async shutdown() {
        this.state.assertCurrentState('Initialised');
        await this.shutdownInternal();
        this.state.triggerEvent('shutdown');
    }
    // ######## Me management ########
    /**
     * Get the someone that represents me.
     */
    async me() {
        this.state.assertCurrentState('Initialised');
        if (this.leute === undefined) {
            throw new Error('Leute model is not initialized');
        }
        return SomeoneModel.constructFromLatestVersion(this.leute.me);
    }
    /**
     * Get the someone that represents me, but don't load the data, yet.
     *
     * In order to use the returned model you have to call one of its load functions first.
     */
    meLazyLoad() {
        this.state.assertCurrentState('Initialised');
        if (this.leute === undefined) {
            throw new Error('Leute model is not initialized');
        }
        return new SomeoneModel(this.leute.me);
    }
    // ######## Other people management ########
    /**
     * Get all other persons you know.
     */
    async others() {
        this.state.assertCurrentState('Initialised');
        if (this.leute === undefined) {
            throw new Error('Leute model is not initialized');
        }
        return Promise.all(this.leute.other.map(SomeoneModel.constructFromLatestVersion));
    }
    /**
     * Get all other persons you know, but don't grab the data, yet.
     *
     * In order to use the returned models you have to call one of its load functions first.
     */
    othersLazyLoad() {
        this.state.assertCurrentState('Initialised');
        if (this.leute === undefined) {
            throw new Error('Leute model is not initialized');
        }
        return this.leute.other.map(idHash => new SomeoneModel(idHash));
    }
    /**
     * Add a new person
     *
     * @param other
     */
    async addSomeoneElse(other) {
        this.state.assertCurrentState('Initialised');
        if (this.leute === undefined) {
            throw new Error('Leute model is not initialized');
        }
        if (this.leute.me === other) {
            throw new Error('You cannot add yourself as other person');
        }
        const others = new Set(this.leute.other);
        others.add(other);
        // Use structuredClone to create a deep copy that's not frozen
        const updatedLeute = structuredClone(this.leute);
        updatedLeute.other = [...others];
        this.leute = updatedLeute;
        await this.saveAndLoad();
    }
    /**
     * Remove a person you know.
     *
     * @param other
     */
    async removeSomeoneElse(other) {
        this.state.assertCurrentState('Initialised');
        if (this.leute === undefined) {
            throw new Error('Leute model is not initialized');
        }
        // Use structuredClone to create a deep copy that's not frozen
        const updatedLeute = structuredClone(this.leute);
        updatedLeute.other = updatedLeute.other.filter(o => o !== other);
        this.leute = updatedLeute;
        await this.saveAndLoad();
    }
    // ######## Identity management ########
    /**
     * Create a new identity for myself with a complete set of keys and a 'default' profile.
     *
     * This will:
     * - Create a new person (random email or the specified one)
     * - Create complete keypairs for person
     * - Create a new instance owned by the new person (random instance name or the specified one)
     * - Create complete keypairs for instance
     * - Create a 'default' profile for the new identity owned by itself
     * - Certify profile with "TrustKeys" certificate issued by your main identity
     * - Certify profile with "AffirmationCertificate" certificate issued by the new identity
     */
    async createCompleteIdentityForMyself(email, instanceName) {
        this.state.assertCurrentState('Initialised');
        if (this.leute === undefined) {
            throw new Error('Leute model is not initialized');
        }
        const me = await this.me();
        // Create the new identity
        const idInfo = await LeuteModel.createIdentityWithInstanceAndKeys(email, instanceName);
        // Add identity first so that the profile creation event has it
        await me.addIdentity(idInfo.personId);
        const defaultProfile = await this.createInitialDefaultProfile(idInfo);
        // Add the profile, so that it is added before this function resolves. (The hook will do
        // the same, but we cannot await on the hook)
        await me.addProfile(defaultProfile.idHash);
        await this.shareVersionsWithEveryone(defaultProfile.idHash);
        // Create certificates and share it
        if (defaultProfile.loadedVersion !== undefined) {
            const trustKeysCert = await this.trust.certify('TrustKeysCertificate', {
                profile: defaultProfile.loadedVersion
            });
            const affirmationCert = await this.trust.affirm(defaultProfile.loadedVersion, idInfo.personId);
            // Share certificates
            await this.shareObjectWithIoM(trustKeysCert.signature.hash);
            await this.shareObjectWithEveryone(affirmationCert.hash);
        }
        return defaultProfile;
    }
    /**
     * Create a new identity for someone with a 'default' profile.
     *
     * This will:
     * - Create a new person (random email or the specified one)
     * - Create an empty 'default' profile for the new identity owned by your main identity
     */
    async createShallowIdentityForSomeone(someoneId, email) {
        this.state.assertCurrentState('Initialised');
        if (this.leute === undefined) {
            throw new Error('Leute model is not initialized');
        }
        const someone = await SomeoneModel.constructFromLatestVersion(someoneId);
        const newPersonId = await createPerson(email);
        await someone.addIdentity(newPersonId);
        return someone.createProfile(newPersonId, await this.myMainIdentity());
    }
    /**
     * Create someone with a completely new identity.
     */
    async createSomeoneWithShallowIdentity(email) {
        this.state.assertCurrentState('Initialised');
        if (this.leute === undefined) {
            throw new Error('Leute model is not initialized');
        }
        const newPersonId = await createPerson(email);
        const newProfile = await ProfileModel.constructWithNewProfile(newPersonId, await this.myMainIdentity());
        // Call the hook (even if it already runs) - this ensures, that the profile was added to
        // a someone object
        await this.addProfile(newProfile.idHash);
        const someone = await this.getSomeone(newPersonId);
        if (someone === undefined) {
            throw new Error('Impossible error: Someone does not exist even though the hook ran');
        }
        return someone.idHash;
    }
    /**
     * Create a new profile for someone.
     *
     * @param personId - The Person for which to create the personId
     * @param ensureSomeoneId - if specified, ensure that this someone object is the found object.
     */
    async createProfileForPerson(personId, ensureSomeoneId) {
        this.state.assertCurrentState('Initialised');
        if (this.leute === undefined) {
            throw new Error('Leute model is not initialized');
        }
        const someone = await this.getSomeone(personId);
        if (someone === undefined) {
            throw new Error('Failed to create profile, because no someone object could be found.');
        }
        return someone.createProfile(personId, await this.myMainIdentity());
    }
    // ######## Group management ########
    /**
     * Create a new group.
     *
     * If it already exist this will return the existing group instead.
     *
     * @param name - If specified use this name, otherwise create a group with a random id.
     * @returns the created group or the existing one if it already existed.
     */
    async createGroup(name) {
        this.state.assertCurrentState('Initialised');
        return this.createGroupInternal(name);
    }
    /**
     * Get a list of groups.
     */
    async groups() {
        this.state.assertCurrentState('Initialised');
        if (this.leute === undefined) {
            throw new Error('Leute model is not initialized');
        }
        return Promise.all(this.leute.group.map(GroupModel.constructFromLatestProfileVersion));
    }
    static async everyoneGroup() {
        try {
            return await GroupModel.constructFromLatestProfileVersionByGroupName(LeuteModel.EVERYONE_GROUP_NAME);
        }
        catch (e) {
            throw new Error(`Everyone group does not exist: ${e.message}`);
        }
    }
    // ######## Misc stuff ########
    /**
     * Return the SomeoneModel identified by the person Id or undefined otherwise.
     * @param personId
     */
    async getSomeone(personId) {
        this.state.assertCurrentState('Initialised');
        if (this.leute === undefined) {
            console.error('getSomeone: Leute model does not seem to be initialized (this.leute is null)');
            return;
        }
        try {
            const entries = await getOnlyLatestReferencingObjsHashAndId(personId, 'Someone');
            // Find the entry that is present in the leute list
            const leute = this.leute;
            const entry = entries.find(e => leute.me === e.idHash || leute.other.includes(e.idHash));
            if (entry === undefined) {
                return undefined;
            }
            return SomeoneModel.constructFromVersion(entry.hash);
        }
        catch (e) {
            if (e.name === 'FileNotFoundError') {
                return;
            }
            throw e;
        }
    }
    async hasProfile(profileId) {
        this.state.assertCurrentState('Initialised');
        if (this.leute === undefined) {
            console.error('hasProfile: Leute model does not seem to be initialized (this.leute is null)');
            return false;
        }
        const someones = await getOnlyLatestReferencingObjsHashAndId(profileId, 'Someone');
        for (const someone of someones) {
            if (this.leute.other.includes(someone.idHash)) {
                return true;
            }
            if (this.leute.me === someone.idHash) {
                return true;
            }
        }
        return false;
    }
    /**
     * Return the main ProfileModel of the SomeoneModel identified by the personId.
     * @param personId
     */
    async getMainProfile(personId) {
        this.state.assertCurrentState('Initialised');
        const someone = await this.getSomeone(personId);
        if (someone === undefined) {
            throw new Error(`No someone found for the given personId: ${personId}`);
        }
        return someone.mainProfile();
    }
    /**
     * Sets a new profile for myself.
     *
     * If the profile has a different identity, than the old one the main identity will change!
     *
     * @param profileHash
     */
    async setMyMainProfile(profileHash) {
        const profile = await getIdObject(profileHash);
        const mySomeone = await this.me();
        const oldIdentity = await mySomeone.mainIdentity();
        const newIdentity = profile.personId;
        if (oldIdentity === newIdentity) {
            await mySomeone.setMainProfile(profileHash);
        }
        else {
            if (!(await isPersonComplete(newIdentity))) {
                throw new Error('Person is not complete!');
            }
            this.beforeMainIdSwitch.emit(oldIdentity, newIdentity);
            await this.givePersonAllRights(newIdentity, newIdentity);
            await mySomeone.setMainProfile(profileHash);
            this.afterMainIdSwitch.emit(oldIdentity, newIdentity);
        }
    }
    /**
     * Get my own main profile.
     */
    async getMyMainProfile() {
        const mySomeone = await this.me();
        return await mySomeone.mainProfile();
    }
    /**
     * Change the main identity by setting a new mainProfile.
     *
     * @param newIdentity
     */
    async changeMyMainIdentity(newIdentity) {
        const mySomeone = await this.me();
        const oldIdentity = await mySomeone.mainIdentity();
        if (!(await isPersonComplete(newIdentity))) {
            throw new Error('Person is not complete!');
        }
        this.beforeMainIdSwitch.emit(oldIdentity, newIdentity);
        await this.givePersonAllRights(newIdentity, newIdentity);
        await mySomeone.setMainIdentity(newIdentity);
        this.afterMainIdSwitch.emit(oldIdentity, newIdentity);
    }
    /**
     * Get my own main identity (at the moment from the main profile).
     */
    async myMainIdentity() {
        const mySomeone = await this.me();
        return mySomeone.mainIdentity();
    }
    /**
     * Add a profile to a someone object already managing this persons identity.
     *
     * If no such someone object exists a new one is created.
     */
    async addProfile(profile) {
        this.state.assertCurrentState('Initialised');
        await this.addProfileFromResult(await getObjectByIdHash(profile));
    }
    /**
     * Get my own instance endpoints.
     *
     * @param mainOnly - If true, then only get endpoints for your main identity.
     */
    async findAllOneInstanceEndpointsForMe(mainOnly = true) {
        this.state.assertCurrentState('Initialised');
        const me = await this.me();
        return me.collectAllEndpointsOfType('OneInstanceEndpoint', mainOnly ? await me.mainIdentity() : undefined);
    }
    /**
     * Get all instance endpoints for person.
     *
     * @param personId
     */
    async findAllOneInstanceEndpointsForPerson(personId) {
        this.state.assertCurrentState('Initialised');
        const someone = await this.getSomeone(personId);
        if (someone === undefined) {
            return [];
        }
        return someone.collectAllEndpointsOfType('OneInstanceEndpoint', personId);
    }
    /**
     * Get instance endpoints from all contacts.
     */
    async findAllOneInstanceEndpointsForOthers() {
        this.state.assertCurrentState('Initialised');
        const others = await this.others();
        const endpoints = await Promise.all(others.map(someone => someone.collectAllEndpointsOfType('OneInstanceEndpoint')));
        return endpoints.reduce((acc, curr) => acc.concat(curr), []);
    }
    /**
     * Collect all remote instances of my other devices.
     */
    async getMyLocalEndpoints(personId) {
        const oneInstanceEndpoints = [];
        const me = await this.me();
        for (const identity of personId === undefined ? me.identities() : [personId]) {
            const instances = await getInstancesOfPerson(identity);
            const instancesMap = new Map(instances.map(instance => [instance.instanceId, instance.local]));
            const endpoints = await me.collectAllEndpointsOfType('OneInstanceEndpoint', identity);
            // Only keep the endpoints for which we do not have a complete keypair => remote
            oneInstanceEndpoints.push(...endpoints.filter(endpoint => {
                const isLocal = instancesMap.get(endpoint.instanceId);
                if (isLocal === undefined) {
                    console.error(`Internal error: We do not have an instance object for the OneInstanceEndpoint, instanceId: ${endpoint.instanceId}`);
                    return false;
                }
                return isLocal;
            }));
        }
        return oneInstanceEndpoints;
    }
    /**
     * Collect all remote instances of my other devices.
     */
    async getInternetOfMeEndpoints() {
        const oneInstanceEndpoints = [];
        const me = await this.me();
        for (const identity of me.identities()) {
            const instances = await getInstancesOfPerson(identity);
            const instancesMap = new Map(instances.map(instance => [instance.instanceId, instance.local]));
            const endpoints = await me.collectAllEndpointsOfType('OneInstanceEndpoint', identity);
            // Only keep the endpoints for which we do not have a complete keypair => remote
            oneInstanceEndpoints.push(...endpoints.filter(endpoint => {
                const isLocal = instancesMap.get(endpoint.instanceId);
                if (isLocal === undefined) {
                    console.error(`Internal error: We do not have an instance object for the OneInstanceEndpoint, instanceId: ${endpoint.instanceId}`);
                    return false;
                }
                return !isLocal;
            }));
        }
        return oneInstanceEndpoints;
    }
    /**
     * Collect all remote instances of everyone else.
     */
    async getInternetOfPeopleEndpoints() {
        return this.findAllOneInstanceEndpointsForOthers();
    }
    /**
     * Collect all IoM and IoP endpoints.
     */
    async getAllRemoteEndpoints() {
        const iomEndpoints = await this.getInternetOfMeEndpoints();
        const iopEndpoints = await this.getInternetOfPeopleEndpoints();
        return [
            ...iomEndpoints.map(endpoint => ({
                endpoint,
                isIoM: true
            })),
            ...iopEndpoints.map(endpoint => ({
                endpoint,
                isIoM: false
            }))
        ];
    }
    /**
     *  Collect all local instances that represent this device.
     *
     *  Note: LeuteModel is probably not the correct place for this ... but instances.ts neither
     */
    async getMyLocalInstances() {
        const me = await this.me();
        const localInstances = [];
        for (const identity of me.identities()) {
            if (!(await hasPersonLocalInstance(identity))) {
                continue;
            }
            const instanceId = await getLocalInstanceOfPerson(identity);
            localInstances.push({
                instanceId,
                instanceCryptoApi: await createCryptoApiFromDefaultKeys(instanceId),
                personId: identity
            });
        }
        return localInstances;
    }
    /**
     *  Collect all local instances that represent this device.
     *
     *  Note: LeuteModel is probably not the correct place for this ... but instances.ts neither
     */
    async getMyMainInstance() {
        const me = await this.me();
        const identity = await me.mainIdentity();
        const instanceId = await getLocalInstanceOfPerson(identity);
        return {
            instanceId,
            instanceCryptoApi: await createCryptoApiFromDefaultKeys(instanceId),
            personId: identity
        };
    }
    /**
     * Get the profile name from the main profile.
     *
     * @param personId
     */
    async getMainProfileDisplayName(personId) {
        this.state.assertCurrentState('Initialised');
        const someone = await this.getSomeone(personId);
        if (someone === undefined) {
            return 'undefined';
        }
        return someone.getMainProfileDisplayName();
    }
    /**
     * Get the profile name from one of the default profiles.
     *
     * It will first try to find the profile that we edited (I am owner).
     * Then it will try to find the profile that the person itself edited (He is owner)
     * Then it will look for a default profile from any owner.
     *
     * @param personId
     */
    async getDefaultProfileDisplayName(personId) {
        this.state.assertCurrentState('Initialised');
        const someone = await this.getSomeone(personId);
        if (someone === undefined) {
            return 'undefined';
        }
        return someone.getDefaultProfileDisplayName(personId, await this.myMainIdentity());
    }
    /**
     * Returns items for pictures that were updated.
     *
     * @param _queryOptions
     */
    async *retrievePersonImagesForJournal(_queryOptions) {
        this.state.assertCurrentState('Initialised');
        const allProfiles = await this.getAllProfiles();
        const imagesWithPersonId = [];
        allProfiles.forEach((profile) => {
            profile.descriptionsOfType('PersonImage').forEach(pi => {
                imagesWithPersonId.push({ personId: profile.personId, image: pi });
            });
        });
        imagesWithPersonId.sort((imageWithPersonId1, imageWIthPersonId2) => {
            return imageWithPersonId1.image.timestamp < imageWIthPersonId2.image.timestamp
                ? 1
                : imageWithPersonId1.image.timestamp > imageWIthPersonId2.image.timestamp
                    ? -1
                    : 0;
        });
        const objectDatas = imagesWithPersonId.map(imageWithPersonId => {
            return {
                channelParticipants: ZERO_HASH,
                channelOwner: ZERO_HASH,
                channelEntryHash: ZERO_HASH,
                id: '',
                creationTime: new Date(imageWithPersonId.image.timestamp),
                creationTimeHash: ZERO_HASH,
                author: imageWithPersonId.personId,
                sharedWith: [],
                data: imageWithPersonId.image,
                dataHash: ZERO_HASH
            };
        });
        yield* objectDatas;
    }
    /**
     * Returns items for statuses that were updated.
     *
     * @param _queryOptions
     */
    async *retrieveStatusesForJournal(_queryOptions) {
        this.state.assertCurrentState('Initialised');
        const allProfiles = await this.getAllProfiles();
        const statusesWithPersonId = [];
        allProfiles.forEach((profile) => {
            profile.descriptionsOfType('PersonStatus').forEach(ps => {
                statusesWithPersonId.push({ personId: profile.personId, status: ps });
            });
        });
        statusesWithPersonId.sort((status1, status2) => {
            return status1.status.timestamp < status2.status.timestamp
                ? 1
                : status1.status.timestamp > status2.status.timestamp
                    ? -1
                    : 0;
        });
        const objectDatas = statusesWithPersonId.map(statusWithPersonId => {
            return {
                channelParticipants: ZERO_HASH,
                channelOwner: ZERO_HASH,
                channelEntryHash: ZERO_HASH,
                id: '',
                creationTime: new Date(statusWithPersonId.status.timestamp),
                creationTimeHash: ZERO_HASH,
                author: statusWithPersonId.personId,
                sharedWith: [],
                data: statusWithPersonId.status,
                dataHash: ZERO_HASH
            };
        });
        yield* objectDatas;
    }
    async shareObjectWithEveryone(object) {
        await this.shareObjectWithGroup(object, await calculateIdHashOfObj({
            $type$: 'Group',
            name: LeuteModel.EVERYONE_GROUP_NAME
        }));
    }
    async shareVersionsWithEveryone(id) {
        await this.shareVersionsWithGroup(id, await calculateIdHashOfObj({
            $type$: 'Group',
            name: LeuteModel.EVERYONE_GROUP_NAME
        }));
    }
    async shareObjectWithIoM(object) {
        await this.shareObjectWithGroup(object, await calculateIdHashOfObj({
            $type$: 'Group',
            name: IoMManager.IoMGroupName
        }));
    }
    async shareVersionsWithIoM(id) {
        await this.shareVersionsWithGroup(id, await calculateIdHashOfObj({
            $type$: 'Group',
            name: IoMManager.IoMGroupName
        }));
    }
    async shareObjectWithGroup(object, group) {
        // Load Group to get its HashGroup hash for reverse map lookup
        const groupResult = await getObjectByIdHash(group);
        await createAccess([
            {
                object,
                person: [],
                hashGroup: [groupResult.obj.hashGroup],
                mode: SET_ACCESS_MODE.ADD
            }
        ]);
    }
    async shareVersionsWithGroup(id, group) {
        // Load Group to get its HashGroup hash for reverse map lookup
        const groupResult = await getObjectByIdHash(group);
        await createAccess([
            {
                id,
                person: [],
                hashGroup: [groupResult.obj.hashGroup],
                mode: SET_ACCESS_MODE.ADD
            }
        ]);
    }
    // ######## Private stuff ########
    /**
     * Create a new group.
     *
     * If it already exist this will return the existing group instead.
     *
     * @param name - If specified use this name, otherwise create a group with a random id.
     * @returns the created group or the existing one if it already existed.
     */
    async createGroupInternal(name) {
        if (this.leute === undefined) {
            throw new Error('Leute model is not initialized');
        }
        const group = await GroupModel.constructWithNewGroup(name);
        if (!this.leute.group.includes(group.groupIdHash)) {
            // Create new Leute object with updated group array
            // Use structuredClone to create a deep copy that's not frozen
            const updatedLeute = structuredClone(this.leute);
            updatedLeute.group.push(group.groupIdHash);
            this.leute = updatedLeute;
            await this.saveAndLoad();
        }
        return group;
    }
    /**
     * Create an identity and an instance and corresponding keys
     */
    static async createIdentityWithInstanceAndKeys(email, instanceName) {
        // Just a note:
        // It is okay to not check if person / instance exists beforehand for strong exception
        // guarantee, because if the person does not exist it is guaranteed, that the instance
        // does not exist (instance depends on the person through owner).
        const personResult = await createPersonWithDefaultKeys(email);
        const instanceResult = await createInstanceWithDefaultKeys(personResult.personId, instanceName);
        return { ...personResult, ...instanceResult };
    }
    /**
     * This gives a person all local rights - at the moment do declare trusted keys.
     *
     * @param beneficiary - The person that gets the rights
     * @param issuer - The person that gives the rights
     * @private
     */
    async givePersonAllRights(beneficiary, issuer) {
        await this.trust.certify('RightToDeclareTrustedKeysForEverybodyCertificate', { beneficiary }, issuer);
        await this.trust.certify('RightToDeclareTrustedKeysForSelfCertificate', { beneficiary }, issuer);
    }
    // ######## Hooks for one.core ########
    /**
     * Add a profile to the respective someone object.
     *
     * This call is registered at one.core for listening for new profiles.
     *
     * @param result
     * @private
     */
    async addProfileFromResult(result) {
        await serializeWithType('addProfile', async () => {
            if (this.leute === undefined) {
                console.error('addProfileFromResult: Leute model does not seem to be initialized' +
                    ' (this.leute is null)');
                return;
            }
            const entries = await getOnlyLatestReferencingObjsHashAndId(result.obj.personId, 'Someone');
            // Find the entry that is present in the leute list
            const leute = this.leute;
            const entry = entries.find(e => leute.me === e.idHash || leute.other.includes(e.idHash));
            const isMe = entry !== undefined && entry.idHash === this.leute.me;
            // If no someone was found, create a new one.
            // Attention: We do currently not check if another someone removed from leute exists for
            // this profile. So if a someone object is removed from the contacts of leute and a new
            // profile is received, the contact will reappear with a new someone.
            if (entry === undefined) {
                const profileModel = await ProfileModel.constructFromResult(result);
                const someoneNew = await SomeoneModel.constructWithNewSomeone(this, await createRandomString(32), profileModel);
                await this.addSomeoneElse(someoneNew.idHash);
            }
            else {
                const someone = await SomeoneModel.constructFromVersion(entry.hash);
                // on sync it could happen that the first profile
                // is not a self-owned one, so when a self-owned
                // profile is synced, we should correct it.
                if (!isMe && result.obj.owner === result.obj.personId) {
                    await someone.upgradeMainProfileIfBetter(result.idHash);
                }
                await someone.addProfile(result.idHash);
            }
            this.onProfileUpdate.emit(result.obj, isMe);
            const profileModel = await ProfileModel.constructFromResult(result);
            const endpoints = profileModel.endpointsOfType('OneInstanceEndpoint');
            // Emit new instance endpoint event (emits it also when it is not new ...)
            for (const endpoint of endpoints) {
                this.onNewOneInstanceEndpoint.emit(endpoint, isMe);
            }
            this.onUpdated.emit();
        });
    }
    /**
     * Add a person to the respective {@link LeuteModel.EVERYONE_GROUP_NAME} group.
     *
     * This call is registered at one.core for listening for new persons.
     *
     * @param result
     * @private
     */
    async addPersonToEveryoneGroup(result) {
        this.everyoneGroupNewPeopleCache.push(result.idHash);
        if (this.everyoneGroupWatchdog.enabled()) {
            this.everyoneGroupWatchdog.restart();
        }
        else {
            this.everyoneGroupWatchdog.enable();
        }
    }
    /**
     * Write the accumulated persons to the everyone group.
     */
    async syncEveryoneGroup() {
        const group = await LeuteModel.everyoneGroup();
        for (const person of this.everyoneGroupNewPeopleCache) {
            if (!group.persons.includes(person)) {
                group.persons.push(person);
            }
        }
        this.everyoneGroupNewPeopleCache = [];
        if (this.everyoneGroupWatchdog.enabled()) {
            this.everyoneGroupWatchdog.disable();
        }
        await group.saveAndLoad();
    }
    /**
     * Updates the this.leute member on a new version.
     *
     * This call is registered at one.core for listening for new leute object versions.
     *
     * @param result
     * @private
     */
    async updateLeuteMember(result) {
        if (isVersionedResultOfType(result, 'Leute')) {
            this.leute = result.obj;
            this.pLoadedVersion = result.hash;
            this.onUpdated.emit();
        }
    }
    // ######## Person name cache ########
    getPersonName(personId) {
        return this.personNameCache.get(personId) || 'N/A';
    }
    async updatePersonNameCache() {
        const me = await this.me();
        const others = await this.others();
        const myMainId = await me.mainIdentity();
        for (const someone of [me, ...others]) {
            const names = await someone.getDefaultProfileDisplayNames(myMainId);
            for (const [personId, name] of names) {
                this.personNameCache.set(personId, name);
            }
        }
    }
    async updatePersonNameCacheForPerson(personId) {
        const someone = await this.getSomeone(personId);
        if (someone === undefined) {
            return;
        }
        const name = await someone.getDefaultProfileDisplayName(personId, await this.myMainIdentity());
        this.personNameCache.set(personId, name);
    }
    /**
     * Creates a 'default' profile for the specified person.
     *
     * It will be owned by the same person.
     *
     * @param idInfo
     * @private
     */
    async createInitialDefaultProfile(idInfo) {
        const personKeys = await getObject(idInfo.personKeys);
        // Note, that the returned profile model is the latest version after the merge, not the
        // exact version that you just wrote. The Problem are CRDTs - unmerged versions are not in
        // the version map, so they are not visible to the app and are not transmitted to other
        // instance.
        // The assumption ist, that no unauthorized person can change this crdt type, because
        // input filtering will prevent that (which does not exist, yet!)
        // Another solution would be to have a separate mechanism (besides version maps) that
        // manages intermediary versions (e.g. explicit tags).
        // Everything depends on how you view versions ... what is a version - what is a
        // document - can you have diverging paths in the same document ... etc.
        return await ProfileModel.constructWithNewProfile(idInfo.personId, idInfo.personId, [
            {
                $type$: 'OneInstanceEndpoint',
                personId: idInfo.personId,
                url: this.commserverUrl,
                instanceId: idInfo.instanceId,
                instanceKeys: idInfo.instanceKeys,
                personKeys: idInfo.personKeys
            }
        ], [
            {
                $type$: 'SignKey',
                key: personKeys.publicSignKey
            }
        ]);
    }
    // ######## private stuff - Load & Save ########
    /**
     * Return all the profiles of all the someones, including my own profiles.
     */
    async getAllProfiles() {
        const someoneModels = [await this.me(), ...(await this.others())];
        const profileModels2d = await Promise.all(someoneModels.map((other) => {
            return other.profiles();
        }));
        return profileModels2d.reduce((prev, next) => {
            return prev.concat(next);
        });
    }
    /**
     * Load the latest someone version.
     */
    async loadLatestVersion() {
        const idHash = await calculateIdHashOfObj({
            $type$: 'Leute',
            appId: 'one.leute'
        });
        const result = await getObjectByIdHash(idHash);
        await this.updateModelDataFromLeute(result.obj, result.hash);
    }
    /**
     * Save the leute to disk and load the latest version.
     *
     * Why is there no pure save() function? The cause are crdts. The object that is eventually
     * written to disk might differ from the current state of this instance. This happens when new
     * data was received via chum since the last load. This means that we don't have a hash
     * representing the current state.
     *
     * TODO: It is possible to write the intermediary state and obtain a hash. So we can implement a
     *       pure save() function. But this requires the lower levels to write the top level object
     *       of the tree and return the corresponding hash to the caller. The
     *       storeVersionedObjectCRDT and the plan interfaces don't support that right now in a easy
     *       to grasp way.
     */
    async saveAndLoad() {
        if (this.leute === undefined) {
            throw new Error('No leute data that could be saved');
        }
        const result = await storeVersionedObject(this.leute);
        await this.updateModelDataFromLeute(result.obj, result.hash);
        this.onUpdated.emit();
    }
    /**
     * Updates the members of the model based on a loaded profile and the version hash.
     *
     * @param leute
     * @param version
     * @private
     */
    async updateModelDataFromLeute(leute, version) {
        this.pLoadedVersion = version;
        this.leute = leute;
    }
}
// ######## private functions ########
function isVersionedResultOfType(versionedObjectResult, type) {
    return versionedObjectResult.obj.$type$ === type;
}
// function isUnversionedResultOfType<T extends OneUnversionedObjectTypeNames>(
//     unversionedObjectResult: UnversionedObjectResult,
//     type: T
// ): unversionedObjectResult is UnversionedObjectResult<OneUnversionedObjectInterfaces[T]> {
//     return unversionedObjectResult.obj.$type$ === type;
// }
//# sourceMappingURL=LeuteModel.js.map